import { useState } from 'react';
import { Radio } from 'antd';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

import { Button, Dialog, Field, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { coBatMfa, khoaLoiChanLuu, ruiRoKhoa } from '../../nghiep-vu/cai-dat';
import type { MaRuiRo } from '../../nghiep-vu/cai-dat';
import { useDichCaiDat } from '../../hooks/useDichCaiDat';
import type { FormCaiDat } from '../../hooks/useFormCaiDat';
import { GiaTriThayDoi } from './GiaTriThayDoi';

const KHOA_RUI_RO = {
  mfa_ngoai_dai_bat: 'caiDat.ruiRo.mfaNgoaiDaiBat',
  mfa_bat: 'caiDat.ruiRo.mfaBat',
  email_ngoai_domain: 'caiDat.ruiRo.emailNgoaiDomain',
} as const satisfies Record<MaRuiRo, string>;

/**
 * Thanh lưu của một form cài đặt và hộp xác nhận đi kèm (ADR-0059 mục 8).
 *
 * - Nút lưu **đếm** thay đổi ("Lưu 3 thay đổi") và chỉ đếm thay đổi thật — gõ
 *   lại đúng giá trị cũ không tính.
 * - Hộp xác nhận liệt kê trước → sau. Thay đổi có thể khoá người dùng nói rõ
 *   hậu quả; thay đổi tự khoá chính mình thì **chặn** nút lưu, vì sau đó không
 *   còn đường nào quay lại màn này để sửa.
 * - `VERSION_MISMATCH` hiện thành dải ngay trên thanh, kèm nút tải lại; bản nháp
 *   **không** mất khi tải lại.
 */
export function ThanhLuuCaiDat({ form }: { form: FormCaiDat }) {
  const { t, locale } = useT();
  const d = useDichCaiDat();
  const [moHop, setMoHop] = useState(false);
  const [lyDo, setLyDo] = useState('');
  const [mfaApDung, setMfaApDung] = useState<'NOW' | 'NEXT_LOGIN'>('NEXT_LOGIN');

  const so = form.thayDoi.length;
  const soLoi = khoaLoiChanLuu(form.loi, form.thayDoi).length;
  // Chỉ bám đáy khi có bản nháp: thanh "Chưa có thay đổi" bám đáy chỉ che dòng cuối của khối phía trên.
  const coNhap = Object.keys(form.nhap).length > 0;
  const ruiRo = form.policy ? ruiRoKhoa(form.thayDoi) : [];
  const biChan = ruiRo.some((r) => r.chan);
  const coMfa = coBatMfa(ruiRo);

  const moXacNhan = () => {
    setLyDo('');
    setMfaApDung('NEXT_LOGIN');
    setMoHop(true);
  };

  const xacNhan = async () => {
    await form.luu({
      ...(lyDo.trim() ? { reason: lyDo.trim() } : {}),
      ...(coMfa ? { mfa_apply: mfaApDung } : {}),
    });
    // Thành công, hay lỗi theo trường / xung đột: đều đóng hộp. Lỗi hiện ngay cạnh
    // dòng sai và trên thanh lưu, nơi người dùng sửa được — không phải trong hộp.
    setMoHop(false);
  };

  return (
    <>
      {form.xungDot && (
        <div
          role="alert"
          className="flex flex-col gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 md:flex-row md:items-center md:justify-between"
        >
          <span className="flex items-start gap-2 text-sm text-warning-700">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
            {t('caiDat.xungDot')}
          </span>
          <Button variant="outline" size="sm" loading={form.dangTaiLai} onClick={form.taiLai}>
            {t('caiDat.taiLaiBanMoi')}
          </Button>
        </div>
      )}
      {form.loiChung && (
        <div role="alert" className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {form.loiChung}
        </div>
      )}

      <div
        className={cn(
          'thanh-luu-cai-dat flex flex-col gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm md:flex-row md:items-center md:justify-between',
          coNhap && 'sticky bottom-0 z-10',
        )}
      >
        <p className="min-w-0 text-sm text-content-secondary" role="status">
          {so === 0
            ? t('caiDat.chuaCoThayDoi')
            : soLoi > 0
              ? t('caiDat.suaLoiTruoc', { so: soVN(soLoi, locale) })
              : t('caiDat.coThayDoi', { so: soVN(so, locale) })}
        </p>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          {coNhap && (
            <Button variant="ghost" size="md" disabled={form.dangLuu} onClick={() => form.boNhap()}>
              {t('caiDat.boThayDoi')}
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            disabled={so === 0 || soLoi > 0 || form.xungDot}
            loading={form.dangLuu}
            onClick={moXacNhan}
          >
            {so === 0 ? t('chung.luu') : t('caiDat.luuSoThayDoi', { so: soVN(so, locale) })}
          </Button>
        </div>
      </div>

      <Dialog
        open={moHop}
        onClose={() => setMoHop(false)}
        title={t('caiDat.xacNhanTieuDe', { so: soVN(so, locale) })}
        width={600}
        destroyOnHidden
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="md" onClick={() => setMoHop(false)}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              disabled={biChan}
              loading={form.dangLuu}
              onClick={() => void xacNhan()}
            >
              {t('caiDat.luuSoThayDoi', { so: soVN(so, locale) })}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 pt-2">
          <ul className="divide-y divide-default rounded border border-default">
            {form.thayDoi.map((c) => (
              <li key={c.key} className="space-y-1 px-3 py-2.5">
                <p className="text-sm font-medium text-content-primary">{d.nhan(c.key)}</p>
                <GiaTriThayDoi def={form.danhMuc.get(c.key)} truoc={c.truoc} sau={c.sau} />
                {c.ve_ke_thua && (
                  <p className="text-xs text-content-muted">{t('caiDat.veKeThuaGhiChu')}</p>
                )}
              </li>
            ))}
          </ul>

          {ruiRo.map((r) => (
            <div
              key={r.ma}
              role={r.chan ? 'alert' : 'note'}
              className={
                r.chan
                  ? 'flex items-start gap-2 rounded border border-danger-200 bg-danger-50 px-3 py-2.5 text-xs leading-relaxed text-danger-700'
                  : 'flex items-start gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2.5 text-xs leading-relaxed text-warning-700'
              }
            >
              <ShieldAlert size={15} className="mt-0.5 shrink-0" aria-hidden />
              <span>{t(KHOA_RUI_RO[r.ma])}</span>
            </div>
          ))}

          {coMfa && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-content-primary">{t('caiDat.mfa.apDungKhi')}</legend>
              <Radio.Group
                value={mfaApDung}
                onChange={(e) => setMfaApDung(e.target.value as 'NOW' | 'NEXT_LOGIN')}
                className="flex flex-col gap-2"
              >
                <Radio value="NEXT_LOGIN">
                  <span className="text-sm text-content-primary">{t('caiDat.mfa.lanDangNhapSau')}</span>
                  <span className="block text-xs text-content-muted">{t('caiDat.mfa.lanDangNhapSauMo')}</span>
                </Radio>
                <Radio value="NOW">
                  <span className="text-sm text-content-primary">{t('caiDat.mfa.ngay')}</span>
                  <span className="block text-xs text-content-muted">{t('caiDat.mfa.ngayMo')}</span>
                </Radio>
              </Radio.Group>
            </fieldset>
          )}

          <Field label={t('caiDat.lyDo')} hint={t('caiDat.lyDoGoiY')}>
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                maxLength={500}
                value={lyDo}
                onChange={(e) => setLyDo(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}
